import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  message
} from "antd";
import moment from "moment";
import SchoolService from "../../services/school";
import { columns } from "./tableConfigs";
import SchoolForm from "./schoolForm";

const { Search, TextArea } = Input;
const Option = Select.Option;

const statusColors = {
  pending: "orange",
  approved: "green",
  rejected: "red"
};

const Schools = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [registrationStatus, setRegistrationStatus] = useState("all");
  const [membershipTier, setMembershipTier] = useState("all");
  const [viewSchool, setViewSchool] = useState(null);
  const [editSchool, setEditSchool] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [rejectSchool, setRejectSchool] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchSchools();
  }, [pageIndex, pageSize, searchText, registrationStatus, membershipTier]);

  const fetchSchools = async () => {
    setLoading(true);

    try {
      const response = await SchoolService.getSchools(
        searchText,
        pageIndex,
        pageSize,
        registrationStatus,
        membershipTier
      );

      setData(response.data || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch schools", error);
      message.error("Failed to load schools");
    } finally {
      setLoading(false);
    }
  };

  const openView = async (school) => {
    try {
      const response = await SchoolService.getSchoolById(school._id);
      setViewSchool(response.data);
    } catch (error) {
      message.error("Failed to load school details");
    }
  };

  const openEdit = async (school) => {
    try {
      const response = await SchoolService.getSchoolById(school._id);
      setEditSchool(response.data);
    } catch (error) {
      message.error("Failed to load school details");
    }
  };

  const handleApprove = async (school) => {
    try {
      await SchoolService.approveSchool(school._id);
      message.success("School approved successfully");
      fetchSchools();
      if (viewSchool && viewSchool._id === school._id) {
        setViewSchool({
          ...viewSchool,
          registrationStatus: "approved",
          isActive: true
        });
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to approve school");
    }
  };

  const handleSave = async (payload) => {
    setEditLoading(true);

    try {
      await SchoolService.updateSchool(editSchool._id, payload);
      message.success("School updated successfully");
      setEditSchool(null);
      fetchSchools();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to update school");
    } finally {
      setEditLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      await SchoolService.rejectSchool(rejectSchool._id, rejectReason);
      message.success("School rejected successfully");
      setRejectSchool(null);
      setRejectReason("");
      fetchSchools();
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to reject school");
    }
  };

  const tableColumns = columns({
    onView: openView,
    onEdit: openEdit,
    onApprove: handleApprove,
    onReject: (school) => setRejectSchool(school)
  });

  return (
    <div className="menu-editor-box card-pad-new menu-cate-list">
      <Card title="Schools Management">
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Search
              placeholder="Search by school, email, city"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPageIndex(0);
              }}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              value={registrationStatus}
              onChange={(value) => {
                setRegistrationStatus(value);
                setPageIndex(0);
              }}
              style={{ width: "100%" }}
            >
              <Option value="all">All Statuses</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Select
              value={membershipTier}
              onChange={(value) => {
                setMembershipTier(value);
                setPageIndex(0);
              }}
              style={{ width: "100%" }}
            >
              <Option value="all">All Tiers</Option>
              <Option value="Small">Small</Option>
              <Option value="Medium">Medium</Option>
              <Option value="Large">Large</Option>
            </Select>
          </Col>
        </Row>

        <Table
          rowKey="_id"
          loading={loading}
          columns={tableColumns}
          dataSource={data}
          pagination={{
            current: pageIndex + 1,
            pageSize,
            total: totalCount,
            showSizeChanger: true
          }}
          onChange={(pagination) => {
            setPageIndex((pagination.current || 1) - 1);
            setPageSize(pagination.pageSize || 10);
          }}
        />
      </Card>

      <Modal
        visible={Boolean(viewSchool)}
        title={viewSchool ? viewSchool.name : "School Details"}
        footer={[
          <Button key="close" onClick={() => setViewSchool(null)}>
            Close
          </Button>
        ]}
        width={850}
        onCancel={() => setViewSchool(null)}
      >
        {viewSchool && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Primary Email">
              {viewSchool.primaryEmail}
            </Descriptions.Item>
            <Descriptions.Item label="Secondary Email">
              {viewSchool.secondaryEmail || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Primary Phone">
              {viewSchool.primaryPhone}
            </Descriptions.Item>
            <Descriptions.Item label="Secondary Phone">
              {viewSchool.secondaryPhone || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {viewSchool.address}
            </Descriptions.Item>
            <Descriptions.Item label="Location">
              {viewSchool.city}, {viewSchool.country}
            </Descriptions.Item>
            <Descriptions.Item label="Membership">
              {viewSchool.membershipTier}
            </Descriptions.Item>
            <Descriptions.Item label="Staff Usage">
              {viewSchool.currentStaffCount || 0}/{viewSchool.staffLimit || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Registration Status">
              <Tag color={statusColors[viewSchool.registrationStatus] || "blue"}>
                {viewSchool.registrationStatus}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Active">
              <Tag color={viewSchool.isActive ? "green" : "red"}>
                {viewSchool.isActive ? "Yes" : "No"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Start Date">
              {viewSchool.startDate
                ? moment(viewSchool.startDate).format("YYYY-MM-DD")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Expiry Date">
              {viewSchool.expiryDate
                ? moment(viewSchool.expiryDate).format("YYYY-MM-DD")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Remaining Days">
              {viewSchool.remainingDays === null ? "N/A" : viewSchool.remainingDays}
            </Descriptions.Item>
            <Descriptions.Item label="Created On">
              {viewSchool.createdAt
                ? moment(viewSchool.createdAt).format("YYYY-MM-DD HH:mm")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {viewSchool.notes || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <SchoolForm
        visible={Boolean(editSchool)}
        school={editSchool}
        loading={editLoading}
        onCancel={() => setEditSchool(null)}
        onSave={handleSave}
      />

      <Modal
        visible={Boolean(rejectSchool)}
        title="Reject School Registration"
        onCancel={() => {
          setRejectSchool(null);
          setRejectReason("");
        }}
        onOk={handleReject}
      >
        <p>Please add a rejection reason for the school record.</p>
        <TextArea
          rows={4}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Reason for rejection"
        />
      </Modal>
    </div>
  );
};

export default Schools;
